import React, { useState } from "react";

const sections = [
  {
    id: "overview",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/>
        <rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/>
      </svg>
    ),
    title: "How the Portal Works",
    content: (
      <div>
        <p style={{marginBottom:16, fontSize:13, color:"var(--text-secondary)", lineHeight:1.6}}>
          The NeuroGen IOM QA Portal automates the review of Intraoperative Neurophysiological Monitoring (IONM)
          documents. When PDFs are uploaded, they go through an AI-powered pipeline that extracts content, detects
          clinical alerts, runs compliance checks, and flags issues for human review.
        </p>
        <div style={{display:"flex", flexDirection:"column", gap:0, marginBottom:20}}>
          {[
            { step:"1", label:"Upload",     desc:"PDFs are uploaded through the portal — as a single combined file, or as multiple source files per case (Event Log, Chat Log, IP Report, Tech Report). The system auto-detects patient ID and source type from the filename." },
            { step:"2", label:"Extract",     desc:"Azure Document Intelligence + PyMuPDF extract all text, tables, and structure from every page using a hybrid approach for maximum coverage." },
            { step:"3", label:"Classify",    desc:"Each file is classified by patient ID (PID) and source type. Filenames like 548721CHAT.pdf are identified as Chat Logs for patient 548721. Combined single-file uploads (e.g. Martinez_Elena_R_03152026.pdf) proceed directly to QA." },
            { step:"4", label:"Detect Alerts",desc:"GPT-4.1 scans the full document for clinically significant events — signal changes, surgeon notifications, anesthesia interventions, and positioning changes. Alerts are categorized by type and severity." },
            { step:"5", label:"Analyse",     desc:"GPT-4.1 extracts patient and procedure metadata, then runs all applicable QA checks. For multi-file cases, QA runs at the PID level after all source files are extracted, merging content from all sources." },
            { step:"6", label:"Index",       desc:"Results are stored in Azure AI Search so they can be queried, filtered, and searched across all cases." },
            { step:"7", label:"Notify",      desc:"If any checks fail, an email notification is sent via the configured Logic App. One notification per case per day — duplicates are suppressed automatically." },
            { step:"8", label:"Review",      desc:"A reviewer opens the case, reviews AI evidence for each failed check (organized by Technologist and IP audit types), records decisions, and submits the review with dual sign-off." },
          ].map((s, i, arr) => (
            <div key={s.step} style={{display:"flex", gap:16, alignItems:"flex-start"}}>
              <div style={{display:"flex", flexDirection:"column", alignItems:"center", flexShrink:0}}>
                <div style={{width:32, height:32, borderRadius:"50%", background:"var(--blue-800)", color:"white", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:600, fontFamily:"var(--font-mono)"}}>{s.step}</div>
                {i < arr.length - 1 && <div style={{width:2, height:28, background:"var(--border)", margin:"4px 0"}}/>}
              </div>
              <div style={{paddingTop:6, paddingBottom:i < arr.length - 1 ? 0 : 0}}>
                <div style={{fontWeight:600, fontSize:13, marginBottom:2}}>{s.label}</div>
                <div style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5}}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{padding:"12px 14px", background:"#f0fdfa", borderRadius:8, border:"1px solid #ccfbf1", marginBottom:12}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"#0f766e", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.06em"}}>Multi-file cases</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5, margin:0}}>
            When multiple files share the same patient ID (e.g. 548721.pdf, 548721CHAT.pdf, 548721IP.pdf, 548721TECH.pdf),
            each file is extracted individually. Once all files are ready, PID-level QA runs automatically —
            merging content from all sources and running the full check set against the combined data. The case
            appears as a single expandable group on the All Cases page.
          </p>
        </div>

        <div style={{padding:"12px 14px", background:"var(--blue-50)", borderRadius:8, border:"1px solid var(--blue-100)"}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--blue-700)", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.06em"}}>Processing time</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5, margin:0}}>
            A typical 35–45 page IONM document takes <strong>10–20 minutes</strong> to process. Each source file
            in a multi-file case processes independently (within the 10-minute Azure Function window), and PID-level
            QA runs in its own window after all files are extracted. A floating status icon tracks progress in real time.
          </p>
        </div>
      </div>
    )
  },
  {
    id: "upload",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M8 10V3"/><polyline points="5 6 8 3 11 6" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M2 12v1a1 1 0 001 1h10a1 1 0 001-1v-1"/>
      </svg>
    ),
    title: "Uploading Documents",
    content: (
      <div>
        <p style={{marginBottom:16, fontSize:13, color:"var(--text-secondary)", lineHeight:1.6}}>
          The Upload page supports two modes. Use the toggle at the top to switch between them.
        </p>

        <div style={{marginBottom:20}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>Filename Patterns</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.6, marginBottom:10}}>
            The system auto-detects patient ID and source type from the filename:
          </p>
          <div style={{display:"flex", flexDirection:"column", gap:6, marginBottom:16}}>
            {[
              { pattern:"548721.pdf",       type:"Event Log",              pid:"548721" },
              { pattern:"548721CHAT.pdf",   type:"Usmon Chat Log",         pid:"548721" },
              { pattern:"548721IP.pdf",     type:"Interpretation Report",  pid:"548721" },
              { pattern:"548721TECH.pdf",   type:"Technical Report",       pid:"548721" },
              { pattern:"Martinez_Elena_R_03152026.pdf", type:"Combined (single file)", pid:"Martinez_Elena_R" },
            ].map(p => (
              <div key={p.pattern} style={{display:"flex", gap:12, padding:"6px 12px", background:"var(--gray-50)", borderRadius:6, border:"1px solid var(--border)", fontSize:12, fontFamily:"var(--font-mono)"}}>
                <span style={{flex:1, color:"var(--text-primary)", fontWeight:500}}>{p.pattern}</span>
                <span style={{color:"var(--text-tertiary)", minWidth:160}}>{p.type}</span>
                <span style={{color:"var(--text-tertiary)", minWidth:120}}>PID: {p.pid}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{marginBottom:20}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>Single File Mode</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.6, marginBottom:10}}>
            Upload one or more individual PDFs. Each file is queued and uploaded in sequence. A progress bar tracks
            the upload for each file. Once uploaded, each document enters the processing pipeline independently.
            For multi-file cases, upload all source files for the same patient — PID-level QA triggers automatically
            once all files are extracted.
          </p>
        </div>

        <div style={{marginBottom:20}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>Batch Upload Mode</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.6, marginBottom:10}}>
            Upload an entire folder or selection of PDFs at once. Files within a batch are staggered automatically
            to avoid rate limit issues — each subsequent document waits up to 4 minutes before starting analysis.
          </p>
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {[
              { label:"Start Batch QA",       desc:'After selecting files, click "Start Batch QA" to begin. A progress bar shows how many documents have completed out of the total.' },
              { label:"Per-file status",       desc:'Each file in the batch shows a green ✓ "Uploaded" badge once its full pipeline is complete.' },
              { label:"Batch history",         desc:'Previous batches are listed in a history table below the active batch. Status and counts are shown for each.' },
              { label:"Resume on refresh",     desc:"If you refresh the page mid-batch, processing continues in the background. The status bar resumes polling automatically on your next visit." },
            ].map(r => (
              <div key={r.label} style={{display:"flex", gap:12, padding:"10px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)"}}>
                <div style={{fontWeight:600, fontSize:13, minWidth:160, flexShrink:0}}>{r.label}</div>
                <div style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5}}>{r.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{marginBottom:16}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"#b45309", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>Conflict detection</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.6, margin:0}}>
            If you upload a file that is currently processing or was recently completed, a <strong>⚠ Files already
            processing</strong> warning appears. You can cancel or click <strong>Upload Anyway</strong> to force
            reprocessing and overwrite existing results.
          </p>
        </div>

        <div style={{padding:"12px 14px", background:"var(--blue-50)", borderRadius:8, border:"1px solid var(--blue-100)"}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--blue-700)", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.06em"}}>Floating status bar</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5, margin:0}}>
            A circular progress icon appears in the bottom-right corner during active processing. Click it to expand
            a panel showing per-file status and elapsed times. It persists across page navigation.
          </p>
        </div>
      </div>
    )
  },
  {
    id: "dashboard",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polyline points="2 11 5 7 8 9 11 4 14 6" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
    title: "Dashboard",
    content: (
      <div>
        <p style={{marginBottom:16, fontSize:13, color:"var(--text-secondary)", lineHeight:1.6}}>
          The dashboard gives a live snapshot of QA activity. All charts and stat cards respond to the time range
          selector. Delta values (↑ ↓) compare the selected period against the equal-length prior period.
          Counts reflect <strong>cases</strong> (PID groups), not individual source files.
        </p>

        <div style={{marginBottom:20}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>Stat Cards</div>
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {[
              { label:"Total Cases",      desc:"Count of cases (Combined files + PID groups) processed in the selected period. An alert badge appears if any cases have clinical alerts. Individual source files (EVT, CHAT, IP, TECH) are not counted separately." },
              { label:"Pass Rate",        desc:"Percentage of cases where all QA checks passed. A case with even one failed check counts as failed." },
              { label:"Avg QA Score",     desc:"Mean score across all cases. Score = passed checks ÷ applicable checks × 100. NOT_APPLICABLE checks are excluded from the calculation." },
              { label:"Review Backlog",   desc:"Number of failed cases with no reviewer decision. Shows a progress bar indicating how many of the failed cases have been reviewed. Green border when backlog is clear, red when items need attention." },
            ].map(c => (
              <div key={c.label} style={{display:"flex", gap:12, padding:"10px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)"}}>
                <div style={{fontWeight:600, fontSize:13, minWidth:150, flexShrink:0}}>{c.label}</div>
                <div style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5}}>{c.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{marginBottom:20}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>Time Range Selector</div>
          <div style={{display:"flex", gap:8, flexWrap:"wrap", marginBottom:10}}>
            {["Today","Week","Month","3 Months","All Time"].map(r => (
              <span key={r} style={{fontSize:11, fontFamily:"var(--font-mono)", padding:"3px 10px", borderRadius:4, background:"var(--gray-100)", border:"1px solid var(--border)", color:"var(--text-secondary)"}}>{r}</span>
            ))}
          </div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5, margin:0}}>
            Chart granularity adapts automatically — hourly for Today, daily for Week/Month,
            weekly for 3 Months, monthly for All Time.
          </p>
        </div>

        <div>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>KPI Charts</div>
          <div style={{display:"flex", flexDirection:"column", gap:10}}>
            {[
              { label:"Case Volume",                 desc:"Area chart of passed (green) vs. failed (red) cases over time. Y-axis uses whole numbers only." },
              { label:"Most Commonly Failed Checks", desc:"Horizontal bar chart ranked by failure count. Use this to spot systematic documentation issues or technologist training gaps." },
              { label:"Review Turnaround",           desc:"Average hours from case upload to completed reviewer sign-off. A rising trend indicates a growing review backlog." },
              { label:"AI Performance",              desc:"Shows AI accuracy (correct decisions ÷ total reviewer decisions), broken down into four categories: AI correct, AI wrong, false positives (AI said FAIL but reviewer overrode to PASS), and missed issues (AI said PASS but reviewer overrode to FAIL). Includes a stacked decision bar and a ranked list of the top 5 checks the AI struggles with most — useful for identifying which prompts or criteria need tightening." },
            ].map(c => (
              <div key={c.label} style={{padding:"12px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)"}}>
                <div style={{fontWeight:600, fontSize:13, marginBottom:4, color:"var(--text-accent)"}}>{c.label}</div>
                <div style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5}}>{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
  {
    id: "documents",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M9 1H4a1 1 0 00-1 1v12a1 1 0 001 1h8a1 1 0 001-1V5L9 1z"/>
        <path d="M9 1v4h4"/><line x1="5" y1="8" x2="11" y2="8"/><line x1="5" y1="11" x2="11" y2="11"/>
      </svg>
    ),
    title: "All Cases",
    content: (
      <div>
        <p style={{marginBottom:16, fontSize:13, color:"var(--text-secondary)", lineHeight:1.6}}>
          The All Cases page lists every processed case with its QA score, status, alerts, and reviewer state.
          Cases are counted at the <strong>PID group level</strong> — a multi-file case (e.g. 3 source files for
          patient 548721) appears as a single expandable row, not 3 separate entries.
        </p>

        <div style={{marginBottom:20}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>PID Grouping</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.6, marginBottom:10}}>
            Files that share the same patient ID are grouped automatically:
          </p>
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {[
              { label:"Group header",      desc:"Shows patient name/PID, file count badge, merged QA score from the PID-level QA run, and aggregate alert status. Click to navigate to the QA review page." },
              { label:"Expand arrow",      desc:"Click the ▶ arrow to expand and see individual source files (EVT, CHAT, IP, TECH) as indented child rows with source type badges." },
              { label:"Child rows",        desc:'Individual source files show "Extracted" status and no QA score — QA runs at the case level, not per-file.' },
              { label:"Auto-trigger",      desc:"When all source files for a PID are extracted, PID-level QA triggers automatically. A \"Pending QA\" badge shows until it completes." },
              { label:"Manual trigger",    desc:'If auto-trigger doesn\'t fire, a blue "Run QA" button appears on the group header.' },
            ].map(r => (
              <div key={r.label} style={{display:"flex", gap:12, padding:"10px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)"}}>
                <div style={{fontWeight:600, fontSize:13, minWidth:130, flexShrink:0}}>{r.label}</div>
                <div style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5}}>{r.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{marginBottom:20}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>Filters</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.6, marginBottom:10}}>
            All filters operate at the <strong>group level</strong> — filtering by status, alerts, procedure, etc.
            shows or hides entire cases, not individual source files within a case.
          </p>
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {[
              { label:"Status pills",     desc:"All / Open / Partial / Passed / Closed — filter by the 4-state review model" },
              { label:"Alerts toggle",     desc:"Shows only cases with clinical alerts. Checks both the PID-level QA doc and child source files for alert status." },
              { label:"Audit Type toggle", desc:"Both / Tech / IP — switches score and rules columns to show per-audit-type data" },
              { label:"Dropdown filters",  desc:"Procedure Type, Facility, Surgeon, Technologist — multi-select with search" },
              { label:"Date Range",        desc:"Filter by the case's processed date. Clear both fields to show all time." },
            ].map(f => (
              <div key={f.label} style={{display:"flex", gap:12, padding:"8px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)"}}>
                <div style={{fontWeight:600, fontSize:13, minWidth:130, flexShrink:0}}>{f.label}</div>
                <div style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5}}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>Case Statuses</div>
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {[
              { badge:"Open",     color:"#ef4444", bg:"rgba(239,68,68,0.1)",  desc:"Has failed checks that need reviewer attention." },
              { badge:"Partial",  color:"#d97706", bg:"rgba(217,119,6,0.1)",  desc:"One audit side (Tech or IP) has been signed off, but the other is still open." },
              { badge:"Passed",   color:"#22c55e", bg:"rgba(34,197,94,0.1)",  desc:"All checks passed. No reviewer action needed." },
              { badge:"Closed",   color:"#6366f1", bg:"rgba(99,102,241,0.1)", desc:"Both audit sides signed off by reviewer(s), or case closed with no failures." },
            ].map(s => (
              <div key={s.badge} style={{display:"flex", gap:14, alignItems:"flex-start", padding:"10px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)"}}>
                <span style={{fontSize:11, fontFamily:"var(--font-mono)", fontWeight:600, color:s.color, background:s.bg, padding:"3px 8px", borderRadius:4, whiteSpace:"nowrap", flexShrink:0, marginTop:1}}>{s.badge}</span>
                <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.6, margin:0}}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
  {
    id: "qa-checks",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polyline points="2 6 5 9 10 3"/><rect x="1" y="1" width="14" height="14" rx="2"/>
      </svg>
    ),
    title: "QA Check Results",
    content: (
      <div>
        <p style={{marginBottom:16, fontSize:13, color:"var(--text-secondary)", lineHeight:1.6}}>
          Each case is evaluated against all active QA checks, organized by <strong>Audit Type</strong> (Technologist
          or Interpreting Physician), then by <strong>Criteria</strong> section. Every check produces one of four results:
        </p>
        <div style={{display:"flex", flexDirection:"column", gap:10, marginBottom:20}}>
          {[
            { result:"PASS",               icon:"✓", color:"#10b981", bg:"rgba(16,185,129,0.1)", desc:"The AI found sufficient evidence the requirement is met and can quote it directly from the document." },
            { result:"FAIL",               icon:"✗", color:"#ef4444", bg:"rgba(239,68,68,0.1)",  desc:"The requirement was not met, or the information was absent after a thorough search. Absence = FAIL." },
            { result:"UNABLE TO DETERMINE",icon:"?", color:"#6b7280", bg:"rgba(107,114,128,0.1)",desc:"Genuine ambiguity — conflicting information, or criteria too vague to evaluate objectively." },
            { result:"NOT APPLICABLE",     icon:"—", color:"#94a3b8", bg:"rgba(148,163,184,0.1)",desc:"The check does not apply to this case — e.g. a modality-specific check when that modality wasn't used, or a source-specific check when the source file wasn't uploaded." },
          ].map(r => (
            <div key={r.result} style={{display:"flex", gap:12, alignItems:"flex-start", padding:"10px 14px", background:r.bg, borderRadius:8, border:`1px solid ${r.color}30`}}>
              <span style={{fontFamily:"var(--font-mono)", fontWeight:700, color:r.color, fontSize:14, flexShrink:0, marginTop:1}}>{r.icon}</span>
              <div>
                <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:r.color, marginBottom:3}}>{r.result}</div>
                <div style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5}}>{r.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{padding:"12px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)", marginBottom:12}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:6}}>QA SCORE CALCULATION</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.6, margin:0}}>
            Score = (Passed checks ÷ Applicable checks) × 100. Only PASS and FAIL count toward the denominator.
            NOT_APPLICABLE and UNABLE TO DETERMINE checks are excluded from scoring.
          </p>
        </div>

        <div style={{padding:"12px 14px", background:"#f0fdfa", borderRadius:8, border:"1px solid #ccfbf1", marginBottom:12}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"#0f766e", marginBottom:6}}>SOURCE BADGES</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5, margin:0}}>
            For multi-file PID cases, each QA check shows a colored source badge indicating which document source
            it evaluates (EVT, CHAT, IP, TECH, SCRN). If the required source file was not uploaded, the badge
            shows an amber ⚠ warning. A source summary banner at the top of the detail page shows which sources
            are available and which are missing.
          </p>
        </div>

        <div style={{padding:"12px 14px", background:"#eff6ff", borderRadius:8, border:"1px solid #bfdbfe"}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"#1d4ed8", marginBottom:6}}>AUDIT TYPES</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5, margin:0}}>
            Checks are split into <strong>Technologist</strong> (50 checks covering event log documentation,
            screenshots, chat log, and tech report) and <strong>Interpreting Physician</strong> (24 checks
            covering IP communication, response times, and interpretation report quality). Each side is
            reviewed and signed off independently.
          </p>
        </div>
      </div>
    )
  },
  {
    id: "reviewing",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M2 8h12M8 2v12" strokeLinecap="round"/>
      </svg>
    ),
    title: "Reviewing a Case",
    content: (
      <div>
        <p style={{marginBottom:16, fontSize:13, color:"var(--text-secondary)", lineHeight:1.6}}>
          When a case fails QA, a reviewer must assess each failed check and record a decision. The case detail
          page shows QA results organized by audit type (Technologist and IP), with clinical alerts and a
          multi-PDF viewer for source files.
        </p>
        <div style={{display:"flex", flexDirection:"column", gap:12, marginBottom:20}}>
          {[
            { n:"1", title:"Open the case",                desc:'From All Cases, click "Review" on any case showing Open or Partial status. For multi-file cases, click the PID group header row.' },
            { n:"2", title:"Review the source summary",    desc:"For PID cases, a banner at the top shows which source files were uploaded (✓ EVT, ✓ CHAT, etc.) and which are missing (⚠). This tells you if any checks may be N/A due to missing sources." },
            { n:"3", title:"View source PDFs",             desc:'Click "View PDF" to open the sidebar. For multi-file cases, tabs (EVT, CHAT, IP, TECH) let you switch between source file PDFs.' },
            { n:"4", title:"Review clinical alerts",       desc:"If the case has alerts, a red alert section appears showing each event with type, severity, timestamp, modality, and quoted evidence." },
            { n:"5", title:"Read the QA checks",           desc:"Checks are organized by audit type, then by criteria section. Failed rules are auto-expanded with AI evidence. Source badges show which file each check evaluates." },
            { n:"6", title:"Record decisions",             desc:'For failed checks: "Override" changes FAIL → PASS (with a required justification reason). "Confirm" confirms the AI was correct. For passed checks: an "Override" button lets you change PASS → FAIL if the AI missed an issue (also requires a justification reason). Overridden checks show a strikethrough on the original result with a colored badge. Use "Confirm All" to bulk-confirm remaining failed checks.' },
            { n:"7", title:"Add notes (optional)",         desc:"Reviewer notes are stored in the audit record and visible to others. Useful for documenting context or escalation reasons." },
            { n:"8", title:"Submit each audit side",       desc:'Once all failed Tech checks have decisions, "✓ Submit" appears on the Technologist section. Same for IP. Each side is submitted independently (dual sign-off).' },
            { n:"9", title:"Mark as Reviewed (pass only)", desc:'If a case passed all checks, a "Mark as Reviewed" button appears instead. This closes the case with no individual check review needed.' },
          ].map(s => (
            <div key={s.n} style={{display:"flex", gap:14, padding:"12px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)"}}>
              <div style={{width:24, height:24, borderRadius:"50%", background:"var(--blue-800)", color:"white", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, fontFamily:"var(--font-mono)", flexShrink:0, marginTop:1}}>{s.n}</div>
              <div>
                <div style={{fontWeight:600, fontSize:13, marginBottom:3}}>{s.title}</div>
                <div style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5}}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{padding:"12px 14px", background:"#ecfeff", borderRadius:8, border:"1px solid #a5f3fc", marginBottom:12}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"#0891b2", marginBottom:4}}>DUAL SIGN-OFF</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5, margin:0}}>
            Technologist and IP audit sections are signed off independently. A case shows as "Partial"
            when one side is complete but the other is still open. Both sides must be submitted for the
            case to reach "Closed" status. The header shows badges for each completed side.
          </p>
        </div>

        <div style={{padding:"12px 14px", background:"rgba(245,158,11,0.08)", borderRadius:8, border:"1px solid rgba(245,158,11,0.25)"}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"#b45309", marginBottom:4}}>UNLOCKING A REVIEW</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5, margin:0}}>
            Once submitted, a review is locked to protect the compliance record. Click <strong>🔓 Unlock Review</strong>
            in the header to clear all decisions and allow re-review. All unlocks are recorded in the audit trail.
          </p>
        </div>
      </div>
    )
  },
  {
    id: "waveform",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <polyline points="2 12 6 6 10 18 14 8 18 14 22 12"/>
      </svg>
    ),
    title: "Waveform Analysis",
    content: (
      <div>
        <p style={{marginBottom:16, fontSize:13, color:"var(--text-secondary)", lineHeight:1.6}}>
          The Waveform Analysis tab provides AI-assisted review of neurophysiological waveform data extracted
          from documents. This feature runs automatically for new documents once enabled.
        </p>
        <div style={{marginBottom:20}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"#0f766e", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>Reading the results</div>
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {[
              { label:"Form selector",    desc:'Pill buttons let you jump between waveform groups. Groups with a voltage alert are highlighted in red.' },
              { label:"Page thumbnails",  desc:"Click any thumbnail to open a full-size lightbox. Use ← → arrow keys to navigate." },
              { label:"Voltage table",    desc:"Lists every timestamp and voltage reading. The first row is marked as baseline. Values ≥10% change are highlighted red." },
              { label:"GPT-4o Finding",   desc:"AI summary of trace quality and notable changes. Always verify against the actual waveform images." },
              { label:"Confidence score", desc:"Scores below 70% (shown in red) should be treated with extra scrutiny." },
            ].map(r => (
              <div key={r.label} style={{display:"flex", gap:12, padding:"10px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)"}}>
                <div style={{fontWeight:600, fontSize:13, minWidth:140, flexShrink:0, color:"#0f766e"}}>{r.label}</div>
                <div style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5}}>{r.desc}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{padding:"12px 14px", background:"rgba(245,158,11,0.08)", borderRadius:8, border:"1px solid rgba(245,158,11,0.25)"}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"#b45309", marginBottom:4}}>ADVISORY ONLY</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5, margin:0}}>
            Waveform analysis is performed by GPT-4o vision and is intended to assist human review, not replace it.
            All findings must be confirmed by a qualified reviewer.
          </p>
        </div>
      </div>
    )
  },
  {
    id: "audit-reports",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M13 2H3a1 1 0 00-1 1v10a1 1 0 001 1h10a1 1 0 001-1V3a1 1 0 00-1-1z"/>
        <line x1="5" y1="5" x2="11" y2="5"/><line x1="5" y1="8" x2="11" y2="8"/><line x1="5" y1="11" x2="8" y2="11"/>
      </svg>
    ),
    title: "Audit Reports",
    content: (
      <div>
        <p style={{marginBottom:16, fontSize:13, color:"var(--text-secondary)", lineHeight:1.6}}>
          Generate compliance reports for four different audiences. Select a report type, configure date range
          and filters, preview results, then export as PDF or CSV. Reports count cases (not individual source files)
          and scores exclude NOT_APPLICABLE checks.
        </p>
        <div style={{marginBottom:20}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>Report Types</div>
          <div style={{display:"flex", flexDirection:"column", gap:10}}>
            {[
              { label:"Internal QA Report",            color:"#2563eb", desc:"Monthly/quarterly performance review. Includes action items, top failed checks, technologist performance, documentation compliance, and full case log." },
              { label:"Accreditation (ABRET/ASNM)",    color:"#0d9488", desc:"Formal QA documentation for accreditation bodies. Includes attestation statement, modality coverage, category compliance, CNIM-ready tech logs, and signature block." },
              { label:"Hospital / Surgery Center",     color:"#7c3aed", desc:"Service quality report for hospital administrators. Includes cases by facility, by surgeon, failed case disposition, and alert tracking." },
              { label:"Medicare / Payer Audit",        color:"#b45309", desc:"CPT 95940 / G0453 compliance. Includes per-case IONM START/STOP times (extracted from QA check evidence), monitoring duration, estimated billing units, and documentation gaps." },
            ].map(r => (
              <div key={r.label} style={{padding:"12px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)"}}>
                <div style={{fontWeight:600, fontSize:13, marginBottom:4, color:r.color}}>{r.label}</div>
                <div style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5}}>{r.desc}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{display:"flex", gap:10, marginBottom:16}}>
          <div style={{flex:1, padding:"10px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)"}}>
            <div style={{fontWeight:600, fontSize:13, marginBottom:3}}>PDF Export</div>
            <div style={{fontSize:12, color:"var(--text-secondary)", lineHeight:1.5}}>Opens in a new tab formatted for print. Each report type has a distinct layout tailored to its audience.</div>
          </div>
          <div style={{flex:1, padding:"10px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)"}}>
            <div style={{fontWeight:600, fontSize:13, marginBottom:3}}>Excel (CSV)</div>
            <div style={{fontSize:12, color:"var(--text-secondary)", lineHeight:1.5}}>Downloads a CSV with multiple sections — summary, breakdowns, case detail, and data gaps.</div>
          </div>
        </div>
        <div style={{padding:"12px 14px", background:"var(--blue-50)", borderRadius:8, border:"1px solid var(--blue-100)"}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--blue-700)", marginBottom:4}}>FILE AUDIT</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5, margin:0}}>
            Select individual cases from the All Cases page and click "Generate Report" to create a per-case
            audit with detailed QA results. For PID groups, select the group header checkbox to audit the
            case as a single entity (not individual source files).
          </p>
        </div>
      </div>
    )
  },
{
    id: "admin-qa-checks",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <path d="M12 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V3a1 1 0 00-1-1z"/>
        <path d="M6 5h4M6 8h4M6 11h2"/>
      </svg>
    ),
    title: "Admin: QA Checks",
    content: (
      <div>
        <p style={{marginBottom:16, fontSize:13, color:"var(--text-secondary)", lineHeight:1.6}}>
          Administrators can manage QA criteria directly from the portal without editing CSV files
          in Azure Storage. This section is only visible to users with the <strong>Admin</strong> role.
        </p>
        <div style={{marginBottom:20}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>Managing checks</div>
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {[
              { label:"View all checks",      desc:"A searchable, filterable table of all 74 QA checks showing ID, question, audit type (Tech/IP), criteria group, required sources, and active status." },
              { label:"Filter & search",       desc:"Filter by All, Tech, IP, or Inactive. Use the search box to find checks by question text, criteria name, or check ID." },
              { label:"Edit a check",          desc:'Click "Edit" to expand the inline editor. Modify the question, audit type, category, criteria group, required sources, modality, sort order, or max points.' },
              { label:"Toggle active/inactive", desc:"Click the toggle switch to deactivate a check. Inactive checks are dimmed with strikethrough and skipped during QA processing. Reactivate at any time." },
              { label:"Add new check",         desc:'Click "+ Add check" to open a blank form. The system auto-assigns the next available ID. Fill in the question and fields, then save.' },
              { label:"Save to blob",          desc:"Changes are held in memory until you click \"Save to blob\". This writes the updated CSV to Azure Storage — the next document upload will use the new rules." },
            ].map(r => (
              <div key={r.label} style={{display:"flex", gap:12, padding:"10px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)"}}>
                <div style={{fontWeight:600, fontSize:13, minWidth:160, flexShrink:0}}>{r.label}</div>
                <div style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5}}>{r.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{marginBottom:20}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"#534AB7", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>AI-assisted question improvement</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.6, marginBottom:10}}>
            When adding or editing a check, click <strong>"Improve with AI"</strong> to have GPT-4.1 suggest
            three improved versions of your question, ranked by specificity. Each suggestion includes an
            explanation of why it's better. Select one and click "Use selected suggestion" to replace your draft.
            You can still edit the suggestion before saving.
          </p>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.6, margin:0}}>
            This helps ensure check questions are specific, measurable, and unambiguous — reducing false
            positives and missed issues in the AI's QA evaluation.
          </p>
        </div>

        <div style={{marginBottom:16}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>Version history & rollback</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.6, margin:0}}>
            Every save creates a new version automatically (via Azure Blob versioning). Click
            <strong> "Version history"</strong> to see all previous versions with timestamps and who made
            each change. Click <strong>"Restore"</strong> on any version to roll back. The current version
            is always shown in the status bar at the bottom of the page.
          </p>
        </div>

        <div style={{padding:"12px 14px", background:"rgba(245,158,11,0.08)", borderRadius:8, border:"1px solid rgba(245,158,11,0.25)"}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"#b45309", marginBottom:4}}>ADMIN ROLE REQUIRED</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5, margin:0}}>
            The QA Checks page is only visible to users assigned the Admin role in Microsoft Entra ID.
            Standard QA Reviewers (Member role) will not see the Admin section in the sidebar.
          </p>
        </div>
      </div>
    )
  },

  {
    id: "ai-assistant",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14 8c0 3.314-2.686 6-6 6a5.99 5.99 0 01-3-0.8L1 14l0.8-4A6 6 0 1114 8z"/>
        <circle cx="5.5" cy="8" r="0.75" fill="currentColor" stroke="none"/>
        <circle cx="8"   cy="8" r="0.75" fill="currentColor" stroke="none"/>
        <circle cx="10.5" cy="8" r="0.75" fill="currentColor" stroke="none"/>
      </svg>
    ),
    title: "AI Assistant",
    content: (
      <div>
        <p style={{marginBottom:16, fontSize:13, color:"var(--text-secondary)", lineHeight:1.6}}>
          Ask natural language questions about any case or across all cases. When opened from a case detail
          page via <strong>Ask AI →</strong>, answers are scoped to that case's full content automatically.
          For PID cases, the AI has access to merged content from all source files.
        </p>
        <div style={{marginBottom:16}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"var(--text-accent)", marginBottom:8, textTransform:"uppercase", letterSpacing:"0.06em"}}>Example questions</div>
          <div style={{display:"flex", flexDirection:"column", gap:6}}>
            {[
              "What were the QA results for this case?",
              "What time did IONM start and stop?",
              "Did the surgeon get notified of any alerts?",
              "Which cases failed the baseline documentation check?",
              "What checks are most commonly failing across all cases?",
              "What modalities were used in this procedure?",
              "Summarise the recording events for this case.",
              "What did the IP say about the signal change at 08:30?",
            ].map(q => (
              <div key={q} style={{fontSize:12, fontFamily:"var(--font-mono)", padding:"6px 10px", background:"var(--gray-50)", borderRadius:6, border:"1px solid var(--border)", color:"var(--text-secondary)"}}>"{q}"</div>
            ))}
          </div>
        </div>
        <div style={{padding:"12px 14px", background:"rgba(245,158,11,0.08)", borderRadius:8, border:"1px solid rgba(245,158,11,0.25)"}}>
          <div style={{fontSize:12, fontWeight:600, fontFamily:"var(--font-mono)", color:"#b45309", marginBottom:4}}>IMPORTANT</div>
          <p style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5, margin:0}}>
            AI answers are based on indexed content and may not be exhaustive. Always verify critical findings
            against the original PDF before making compliance decisions.
          </p>
        </div>
      </div>
    )
  },
  {
    id: "identity",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="8" cy="5" r="3"/><path d="M1 14c0-3.314 3.134-6 7-6s7 2.686 7 6"/>
      </svg>
    ),
    title: "Identity & Accountability",
    content: (
      <div>
        <p style={{marginBottom:16, fontSize:13, color:"var(--text-secondary)", lineHeight:1.6}}>
          The portal uses your Microsoft account to track all actions for compliance and accountability.
          Technologist and surgeon identity is extracted directly from the PDF — not from who uploaded the file.
        </p>
        <div style={{display:"flex", flexDirection:"column", gap:10}}>
          {[
            { label:"Uploader",             desc:"Your name is recorded when you submit a document. Captured from your Microsoft account sign-in." },
            { label:"Technologist",         desc:"Extracted from the 'Technologist:' field in the PDF header. This is the person who performed monitoring — separate from the uploader." },
            { label:"Surgeon",              desc:"Extracted from the 'Surgeon:' field. Used in Hospital reports and case filters." },
            { label:"Monitoring Neurologist",desc:"Extracted from the 'Monitoring Neurologist:' or 'Supervising Neurologist:' field." },
            { label:"Reviewer",             desc:"Your name is attached to every decision you save and every review you submit. Both Tech and IP sign-offs record the reviewer independently." },
          ].map(i => (
            <div key={i.label} style={{padding:"12px 14px", background:"var(--gray-50)", borderRadius:8, border:"1px solid var(--border)"}}>
              <div style={{fontWeight:600, fontSize:13, marginBottom:4, color:"var(--text-accent)"}}>{i.label}</div>
              <div style={{fontSize:13, color:"var(--text-secondary)", lineHeight:1.5}}>{i.desc}</div>
            </div>
          ))}
        </div>
      </div>
    )
  },
  {
    id: "faq",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="8" cy="8" r="6"/><path d="M6.5 6a1.5 1.5 0 013 0c0 1.5-1.5 1.5-1.5 3"/><circle cx="8" cy="12" r=".5" fill="currentColor" stroke="none"/>
      </svg>
    ),
    title: "FAQ",
    content: (
      <div style={{display:"flex", flexDirection:"column", gap:12}}>
        {[
          { q:"How long does processing take?",
            a:"A typical 35–45 page document takes 10–20 minutes. Each source file in a multi-file case processes independently. PID-level QA runs automatically after all source files are extracted, in its own processing window. Batch uploads stagger automatically — each document waits up to 4 minutes before starting." },
          { q:"What are multi-file cases?",
            a:"When multiple files share the same patient ID (e.g. 548721.pdf, 548721CHAT.pdf, 548721IP.pdf), they are grouped as a single case. Each file is extracted independently, then PID-level QA merges all content and runs the full check set. The case appears as one expandable row on the All Cases page." },
          { q:"Why does a case show 'Pending QA' instead of a score?",
            a:"For multi-file cases, individual source files are extracted first (shown as 'Extracted' status). PID-level QA triggers automatically once all files are ready. If it hasn't triggered, click the 'Run QA' button on the group header." },
          { q:"What do the source badges mean (EVT, CHAT, IP, TECH)?",
            a:"Each QA check is associated with a required source document. The badge shows which source the check evaluates. An amber ⚠ on a badge means that source file wasn't uploaded, so the check may show as N/A." },
          { q:"What is dual sign-off?",
            a:"Technologist checks and IP checks are reviewed independently. Each side has its own 'Submit' button. A case shows as 'Partial' when one side is done. Both sides must be submitted to reach 'Closed' status." },
          { q:"What does the 'Confirm All' button do?",
            a:"It bulk-confirms all remaining unaddressed failed checks as CONFIRMED_FAIL. A confirmation dialog shows how many checks will be affected. This saves time when you've reviewed the evidence and agree all failures are legitimate." },
          { q:"Can I upload a file that's already been processed?",
            a:"Yes, but the portal will warn you with a ⚠ conflict notice. Re-uploading restarts processing and overwrites existing results. Click 'Upload Anyway' to proceed." },
          { q:"Why does the same case sometimes get different scores?",
            a:"The AI runs at temperature 0 (deterministic), so the same content produces the same score. Different scores usually mean the QA ruleset was updated between uploads, or the document content changed." },
          { q:"Can I edit a review after submitting it?",
            a:'Click "🔓 Unlock Review" in the case header to clear all decisions and start over. All unlocks are recorded in the audit trail.' },
          { q:"What happens to cases that pass QA?",
            a:'"Mark as Reviewed" lets you sign off without reviewing individual checks. Cases remain searchable and appear in all audit reports.' },
          { q:"Why are some fields showing as Unknown in reports?",
            a:"Technologist, surgeon, facility, and monitoring neurologist are extracted from the PDF header. Documents processed before this feature was deployed show Unknown. Reprocess to populate." },
          { q:"What does the AI Performance card show?",
            a:"It replaces the old AI Override Rate card. Shows overall AI accuracy, four metric tiles (correct, wrong, false positives, missed issues), a stacked decision breakdown bar, and the top 5 checks the AI struggles with most. A health badge shows Healthy (≥80%), Needs attention (60–80%), or Review prompts (<60%). Metrics require at least a few reviewer decisions to be meaningful." },
          { q:"What does 'Override to FAIL' mean?",
            a:"When the AI marks a check as PASS but a reviewer finds the documentation is actually insufficient, they can override it to FAIL. This requires selecting a justification reason (e.g. 'AI missed a documentation gap') and optionally adding details. The original PASS is shown with strikethrough and the score recalculates. These overrides feed into the 'missed issues' count on the AI Performance card." },
          { q:"How do I add or edit QA checks?",
            a:"Admins can manage checks from the Admin > QA Checks page. Add new checks, edit existing ones, toggle active/inactive, and use AI to improve question phrasing. Changes are saved to blob storage and take effect on the next document upload. Version history allows rollback to any previous version." },
          { q:"Why don't I see the Admin section in the sidebar?",
            a:"The Admin section is only visible to users with the Admin role assigned in Microsoft Entra ID. If you should have admin access, ask your organization's IT administrator to add you to the Admin security group." },
          { q:"How are scores calculated?",
            a:"Score = (Passed checks ÷ Applicable checks) × 100. NOT_APPLICABLE checks are excluded. Pass Rate measures what percentage of cases had zero failures — even one failed check means the case 'failed' QA." },
        ].map((faq, i) => (
          <details key={i} style={{borderRadius:8, border:"1px solid var(--border)", overflow:"hidden"}}>
            <summary style={{padding:"12px 14px", fontSize:13, fontWeight:600, cursor:"pointer", background:"var(--gray-50)", userSelect:"none", listStyle:"none", display:"flex", justifyContent:"space-between", alignItems:"center"}}>
              {faq.q}
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0, marginLeft:8}}><path d="M2 4l4 4 4-4"/></svg>
            </summary>
            <div style={{padding:"12px 14px", fontSize:13, color:"var(--text-secondary)", lineHeight:1.6, borderTop:"1px solid var(--border)"}}>
              {faq.a}
            </div>
          </details>
        ))}
      </div>
    )
  },
];

export default function Guide() {
  const [active, setActive] = useState("overview");

  return (
    <>
      <div className="page-header">
        <div className="page-title-group">
          <div className="page-title">User Guide</div>
          <div className="page-subtitle">How to use the NeuroGen IOM QA Portal</div>
        </div>
      </div>

      <div className="page-body" style={{display:"flex", gap:20, alignItems:"flex-start"}}>
        <div style={{width:210, flexShrink:0, position:"sticky", top:16}}>
          <div className="card" style={{padding:"8px 0"}}>
            {sections.map(s => (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                style={{
                  display:"flex", alignItems:"center", gap:10, width:"100%", textAlign:"left",
                  padding:"8px 14px", background: active === s.id ? "var(--blue-50)" : "none",
                  border:"none", cursor:"pointer", fontSize:12,
                  fontWeight: active === s.id ? 600 : 400,
                  color: active === s.id ? "var(--blue-800)" : "var(--text-secondary)",
                  borderLeft: active === s.id ? "2px solid var(--blue-800)" : "2px solid transparent",
                  transition:"all 0.15s"
                }}
              >
                <span style={{
                  opacity: active === s.id ? 1 : 0.5, flexShrink:0,
                  color: s.id === "waveform" && active === s.id ? "#0d9488"
                       : s.id === "audit-reports" && active === s.id ? "#2563eb"
                       : "inherit"
                }}>{s.icon}</span>
                {s.title}
              </button>
            ))}
          </div>
        </div>

        <div style={{flex:1, minWidth:0}}>
          {sections.map(s => (
            <div key={s.id} style={{display: active === s.id ? "block" : "none"}}>
              <div className="card">
                <div className="card-header" style={
                  s.id === "waveform"      ? {borderLeft:"3px solid #14b8a6"} :
                  s.id === "audit-reports" ? {borderLeft:"3px solid #2563eb"} : {}
                }>
                  <div style={{display:"flex", alignItems:"center", gap:10}}>
                    <span style={{
                      color: s.id === "waveform"      ? "#0d9488"
                           : s.id === "audit-reports" ? "#2563eb"
                           : "var(--blue-800)"
                    }}>{s.icon}</span>
                    <div className="card-title" style={{fontSize:15}}>{s.title}</div>
                  </div>
                </div>
                <div className="card-body" style={{padding:"18px 20px"}}>
                  {s.content}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}